import React from 'react';
import { Check, Inbox } from 'lucide-react';
import { Tooltip } from '../../ui';
import { OutreachAction } from './types';
import { useReadOnly } from '../../../contexts/WizardAccessContext';

/**
 * One of Robbie's moves, compressed to a pill on the moves bar. The title is
 * all that fits, so the reasoning and the exact ask ride in the tooltip — the
 * bar buys the conversation its height back, and nothing Robbie knows is lost.
 *
 * Tapping stages that ask into the box, exactly as its card did; the counter is
 * the one exception and opens its confirm step instead. A pill whose ask is
 * already in the box goes subdued and says so, because the card it replaces
 * stayed on screen until the buyer's send closed it.
 *
 * A viewer sees the same moves as a plain chip: the move is real information
 * about the thread, but there is no box of theirs to put it in.
 */
interface MovePillProps {
  action: OutreachAction;
  /** Its ask is sitting in the composer, unsent. */
  staged: boolean;
  /** Wrapped off the first line: clipped, so it must not be a tab stop. */
  clipped?: boolean;
  /** Its confirm step is open below/above the bar. */
  active?: boolean;
  /** Opens the confirm step rather than staging — the counter only. */
  needsConfirm?: boolean;
  /** The buyer has set this counter's number; the bundle may carry it now. */
  confirmed?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const MovePill: React.FC<MovePillProps> = ({
  action,
  staged,
  clipped,
  active,
  needsConfirm,
  confirmed,
  disabled,
  onClick,
}) => {
  const readOnly = useReadOnly();
  const tone = staged
    ? 'border-line bg-canvas text-ink-muted'
    : active
      ? 'border-robbie bg-robbie-bg text-robbie-ink'
      : confirmed
        ? 'border-action-border bg-action-bg text-action-deep'
        : 'border-robbie-border bg-surface text-ink hover:border-robbie';

  return (
    <Tooltip
      className="shrink-0"
      content={
        <>
          <span className="block font-semibold">{action.title}</span>
          {action.rationale && <span className="mt-s1 block">{action.rationale}</span>}
          <span className="mt-s1 block text-white/70">
            {readOnly
              ? `Robbie's ask: “${action.suggestedAsk}”`
              : staged
              ? 'Already in your box, unsent — change it there, then send.'
              : needsConfirm
                ? 'Set the number first — I won’t write a price you haven’t confirmed into your box.'
                : confirmed
                  ? 'You set this number — open it again to change it, or put it in your box.'
                  : `Puts in your box: “${action.suggestedAsk}”`}
          </span>
        </>
      }
    >
      {readOnly ? (
        <span
          className={`inline-flex max-w-[190px] items-center gap-s2 rounded-pill border border-robbie-border bg-surface px-[9px] py-[3px] text-meta font-semibold text-ink ${
            clipped ? 'pointer-events-none' : ''
          }`}
        >
          <span className="truncate">{action.title}</span>
        </span>
      ) : (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          tabIndex={clipped ? -1 : undefined}
          aria-hidden={clipped || undefined}
          className={`inline-flex max-w-[190px] items-center gap-s2 rounded-pill border px-[9px] py-[3px] text-meta font-semibold disabled:pointer-events-none disabled:opacity-50 ${tone} ${
            clipped ? 'pointer-events-none' : ''
          }`}
        >
          {staged && <Inbox className="h-[11px] w-[11px] shrink-0" />}
          {confirmed && <Check className="h-[11px] w-[11px] shrink-0" />}
          <span className="truncate">{action.title}</span>
          {staged && <span className="shrink-0 font-normal text-ink-muted">in your box</span>}
        </button>
      )}
    </Tooltip>
  );
};

export default MovePill;
